import { ApolloClient } from '@apollo/client';
import { History } from 'history';
import { Logger } from '@feature-hub/core';
import { ServerRequestV1 } from '@feature-hub/server-request';
import {
  aggregateSectionMap,
  castQSGContentToNavigation,
  getAllDisclaimers,
  getAvailableModels,
  getQSGContent,
  getQSGNavigation,
  getQSGSearchResults,
} from '../fetch-data/graphql/helpers';
import {
  setAllDisclaimers,
  setAvailableQsgModels,
  setNotificationMessage,
  setQsgContent,
  setQsgNavigation,
  setQsgSearchPage,
  setQsgSearchResults,
  setQsgSearchTerm,
  setQsgSectionMap,
  setSelectedQsgModel,
  setSsrHistory,
} from './slices/qsg/actions';
import {
  findFirstIndexableSection,
  findSectionRecursive,
  isSectionIndexable,
} from '../utils/navigation';
import { getSearchKey, SEARCH_LIMIT } from '../hooks/custom/use-search-loader';
import { NavigationGuide, QsgV1Model } from '../fetch-data/graphql/types';
import { searchAndPaginateResults } from '../utils/search';
import { StoreModel, Action } from './types';
import { rootReducer, getInitialState } from './slices';
import {
  getQueryParams,
  getSectionFromPath,
  getSearchTermFromQuery,
  getSearchPageFromQuery,
  isGuideRoute,
  getModelYearFromHistory,
  getModelIdFromHistory,
  normalizeSectionId,
} from '../utils/query-param-helpers';
import { replaceWithPathname, getHistorySearch } from '../utils/navigation-url';
import { Breakpoints } from '@volkswagen-onehub/components-core';
import { NavigationSection } from '../fetch-data/graphql/types';

export interface LoadInitialDataParams {
  client: ApolloClient<any>;
  history: History;
  currentState?: StoreModel;
  dispatch?: (action: Action) => void;
  setShowGuide?: (v: string) => void;
  logger?: Logger;
  serverRequest?: ServerRequestV1;
}

export const enableDynamicSectionLoad = false;



/**
 * Load initial data for the QSG app and return the new state.
 * This version is used during SSR where we need to build state without React.
 *
 * @returns The new state after loading data
 */
export async function loadInitialDataForSSR(
  client: ApolloClient<any>,
  history: History,
  initialState: StoreModel = getInitialState(),
  logger?: Logger,
  serverRequest?: ServerRequestV1
): Promise<StoreModel> {
  const actions: Action[] = [];
  const applyAction = (action: Action) => {
    actions.push(action);
  };

  await loadInitialDataCore({
    client,
    history,
    dispatch: applyAction,
    logger,
    serverRequest,
  });

  // Apply all actions to build the final state
  return actions.reduce(
    (state, action) => rootReducer(state, action),
    initialState
  );
}

/**
 * Load initial data for the QSG app. This function can be called during SSR or CSR.
 * It updates the store state via dispatch actions (CSR) or collects actions (SSR).
 */
export async function loadInitialData({
  client,
  history,
  dispatch,
  logger,
  serverRequest,
}: LoadInitialDataParams): Promise<void> {
  if (!dispatch) {
    throw new Error('dispatch is required for loadInitialData');
  }

  await loadInitialDataCore({
    client,
    history,
    dispatch,
    logger,
    serverRequest,
  });
}

/**
 * Core logic for loading initial data. This is shared between SSR and CSR versions.
 */
async function loadInitialDataCore({
  client,
  history,
  dispatch,
  logger,
  serverRequest,
}: {
  client: ApolloClient<any>;
  history: History;
  dispatch: (action: Action) => void;
  logger?: Logger;
  serverRequest?: ServerRequestV1;
}): Promise<void> {
  // Use logger if available, otherwise fall back to console
  const log = logger || console;
  const isServer = typeof window === 'undefined';
  const logPrefix = isServer ? '[SSR]' : '[Client]';

  const query = getQueryParams(history);
  const currentSection = isGuideRoute(history.location.pathname)
    ? getSectionFromPath(history.location.pathname)
    : '';
  const searchTerm = getSearchTermFromQuery(query);
  const searchPage = getSearchPageFromQuery(query);
  const searchPageNumber = searchPage || 1;
  const searchKey = getSearchKey(searchTerm, searchPageNumber);
  const isSearch = Boolean(searchTerm);

  const isOnGuideRoute = isGuideRoute(history.location.pathname);
  const modelYear = getModelYearFromHistory(history);
  const modelId = getModelIdFromHistory(history);

  const loadQSG = Boolean(modelId && modelYear && isOnGuideRoute);

  log.debug(
    `${logPrefix} Loading initial data - modelId: ${modelId}, modelYear: ${modelYear}, isOnGuideRoute: ${isOnGuideRoute}, isSearch: ${isSearch}`
  );

  if (isSearch && loadQSG) {
    dispatch(setQsgSearchTerm(searchTerm));
    dispatch(setQsgSearchPage(searchPageNumber));
    log.debug(
      `${logPrefix} Set search term: "${searchTerm}", page: ${searchPageNumber}`
    );
  }

  log.info(
    `${logPrefix} Fetching data from GraphQL - models, disclaimers${loadQSG ? ', QSG content' : ''}${loadQSG && isSearch && enableDynamicSectionLoad ? ', search results' : ''}`
  );

  let [allModels, allDisclaimers, data, searchResults] = await Promise.all([
    getAvailableModels(client),
    getAllDisclaimers(client),
    loadQSG
      ? enableDynamicSectionLoad
        ? getQSGNavigation(modelId, modelYear, client)
        : getQSGContent(modelId, modelYear, client)
      : Promise.resolve(null),
    loadQSG && isSearch && enableDynamicSectionLoad
      ? getQSGSearchResults(
          modelYear,
          modelId,
          searchTerm,
          searchPageNumber,
          SEARCH_LIMIT,
          client
        )
      : Promise.resolve(null),
  ]);

  log.info(
    `${logPrefix} Data fetched - models: ${allModels.length}, disclaimers: ${allDisclaimers.length}, QSG data: ${data ? 'loaded' : 'none'}, search results: ${searchResults ? 'loaded' : 'none'}`
  );

  dispatch(setAvailableQsgModels(allModels));
  dispatch(setAllDisclaimers(allDisclaimers));

  let content: QsgV1Model | null = null;
  let navigation: NavigationGuide | null = null;

  if (data) {
    if (enableDynamicSectionLoad) {
      navigation = data as NavigationGuide;
      dispatch(setQsgNavigation(navigation));
      log.debug(`${logPrefix} Set QSG navigation (dynamic section load)`);
    } else {
      content = data as QsgV1Model;
      navigation = castQSGContentToNavigation(content);
      dispatch(setQsgContent(content));
      dispatch(setQsgNavigation(navigation));

      const sectionMap = aggregateSectionMap(content);
      if (sectionMap) {
        dispatch(setQsgSectionMap(sectionMap));
        log.debug(`${logPrefix} Set QSG section map`);
      }

      if (isSearch) {
        const results = searchAndPaginateResults(
          content,
          searchTerm,
          searchPageNumber,
          SEARCH_LIMIT
        );

        searchResults = results;
        log.debug(`${logPrefix} Generated search results from content`);
      }
    }
  }

  if (searchResults) {
    dispatch(setQsgSearchResults(searchKey, searchResults));

    // Validate that the page doesn't exceed total pages
    const totalCount = searchResults.totalCount;
    const limit = searchResults.limit;
    const totalPages = Math.ceil(totalCount / limit);

    log.debug(
      `${logPrefix} Search results - total: ${totalCount}, page: ${searchPageNumber}/${totalPages}`
    );

    if (searchPageNumber > totalPages && totalPages > 0) {
      // Page exceeds total pages, reset to page 1
      dispatch(setQsgSearchPage(1));
      log.info(
        `${logPrefix} Search page ${searchPageNumber} exceeds total pages (${totalPages}), resetting to page 1`
      );
    }
  }

  if (modelId || modelYear) {
    let existsModel = allModels.find((m) => m.modelId === modelId);
    let existsModelYear = allModels.find((m) => m.modelYear === modelYear);

    const selectedModel = {
      modelId: existsModel ? modelId : '',
      modelYear: existsModel?.modelYear ?? (existsModelYear ? modelYear : ''),
    };

    dispatch(setSelectedQsgModel(selectedModel));
    log.debug(
      `${logPrefix} Set selected model - modelId: ${selectedModel.modelId}, modelYear: ${selectedModel.modelYear}`
    );
  }

  if (navigation) {
    dispatch(setQsgNavigation(navigation));

    const sections = navigation.sections ?? [];
    const foundSection = findSectionRecursive(sections, currentSection);
    const defaultSection = findFirstIndexableSection(sections);

    const validFoundSection =
      foundSection && isSectionIndexable(foundSection) ? foundSection : null;

    console.log('validFoundSection', validFoundSection);

    // Section is now read directly from URL path, no need to dispatch to store
    if (currentSection && validFoundSection) {
      log.debug(
        `${logPrefix} Current section from URL: ${validFoundSection.sectionId}`
      );
    }  else {
      // Redirect to default section on desktop if no valid section is in URL
      redirectToDefaultSection(
        history,
        defaultSection,
        isSearch,
        isServer,
        logger
      );
    }
  } else if (isOnGuideRoute) {
    // On guide route but no data - navigate to model selector
    log.warn(
      `${logPrefix} On guide route but no navigation data available, navigating to model selector`
    );
    history.push('/');

    if (modelId && modelYear)
      dispatch(
        setNotificationMessage(
          `The Quick Start Guide is not available for all vehicles yet`
        )
      );
  }

  // Store history properties during SSR for debugging
  if (isServer) {
    dispatch(
      setSsrHistory({
        pathname: history.location.pathname,
        search: history.location.search,
        hash: history.location.hash,
        serverRequestUrl: serverRequest?.url,
        headers: serverRequest?.headers || {},
      })
    );
    log.info(
      `${logPrefix} Stored SSR history: ${history.location.pathname}${history.location.search}${history.location.hash}, serverRequestUrl: ${serverRequest?.url || 'not available'}, headers: ${Object.keys(serverRequest?.headers || {}).join(', ')}`
    );
  }

  log.info(`${logPrefix} Initial data loading completed`);
}

export function redirectToDefaultSection(
  history: History,
  defaultSection: NavigationSection | null,
  isSearch: boolean,
  isServer: boolean,
  logger?: Logger
): boolean {
  const modelYear = getModelYearFromHistory(history);
  const modelId = getModelIdFromHistory(history);

  // Only redirect on client-side, desktop, when no valid section is in URL, not in search, and default section exists
  if (
    isServer ||
    !defaultSection ||
    isSearch ||
    !modelId ||
    !modelYear
  ) 
    return false;
  

  const isDesktop =
    typeof window !== 'undefined' &&
    window.innerWidth >= Breakpoints.b1280;

  if (!isDesktop) 
    return false;
  
  const normalizedSectionId = normalizeSectionId(defaultSection.sectionId);
  const newPath = `/${modelYear}/${modelId}/${normalizedSectionId}`;
  const searchWithQuestion = getHistorySearch(history);
  const search = searchWithQuestion.startsWith('?')
    ? searchWithQuestion.slice(1)
    : searchWithQuestion;

  const log = logger || console;
  const logPrefix = isServer ? '[SSR]' : '[Client]';
  log.info(
    `${logPrefix} Redirecting to default section on desktop: ${normalizedSectionId}`
  );
  replaceWithPathname(history, newPath, search);
  return true;
}